import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";

export default function Transfer() {
  const navigate = useNavigate();
  const { warehouses, productMap, warehouseMap } = useMaster();
  const [transfers, setTransfers] = useState([]);
  const [open, setOpen] = useState(false);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([{ product_id: "", qty: "" }]);
  const [stockRows, setStockRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/transfers").then((r) => setTransfers(r.data)).catch(() => {});
  };

  useEffect(load, []);

  useEffect(() => {
    if (!fromId) {
      setStockRows([]);
      return;
    }
    api.get(`/reports/stock?warehouse_id=${fromId}`).then((r) => setStockRows(r.data.rows)).catch(() => {});
  }, [fromId]);

  const availMap = Object.fromEntries(stockRows.map((r) => [r.product_id, r.qty]));

  const openDialog = () => {
    setFromId(warehouses[0]?.id || "");
    setToId(warehouses[1]?.id || "");
    setNote("");
    setItems([{ product_id: "", qty: "" }]);
    setOpen(true);
  };

  const setItem = (i, patch) => {
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };

  const submit = async (e) => {
    e.preventDefault();
    const validItems = items.filter((it) => it.product_id && parseInt(it.qty, 10) > 0);
    if (validItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/transfers", {
        from_warehouse_id: fromId,
        to_warehouse_id: toId,
        note,
        items: validItems.map((it) => ({ product_id: it.product_id, qty: parseInt(it.qty, 10) })),
      });
      toast.success(`Delivery note ${data.sj_no} created (Draft)`);
      setOpen(false);
      navigate(`/transfer/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="transfer-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Delivery Notes</h1>
          <p className="mt-1 text-sm text-zinc-500">Move stock between warehouses: Draft → Sent → Received.</p>
        </div>
        <Button data-testid="btn-create-transfer" onClick={openDialog} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Create Delivery Note
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="transfer-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Doc No.</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Route</th>
              <th className="px-4 py-3 text-right font-medium">Items</th>
              <th className="px-4 py-3 font-medium">Created By</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {transfers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-400" data-testid="transfer-empty">
                  No delivery notes yet
                </td>
              </tr>
            )}
            {transfers.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/transfer/${t.id}`)}
                className={`cursor-pointer border-b border-zinc-100 last:border-0 transition-colors hover:bg-zinc-50 ${t.deleted ? "opacity-50" : ""}`}
                data-testid={`transfer-row-${t.sj_no}`}
              >
                <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-600">
                  {t.sj_no}
                  {t.deleted && (
                    <span className="block font-sans text-[10px] font-semibold text-red-500" data-testid={`transfer-deleted-label-${t.sj_no}`}>
                      deleted by {t.deleted_by_name || "-"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(t.created_at)}</td>
                <td className="px-4 py-2.5 text-zinc-700">
                  <span className="inline-flex items-center gap-1.5">
                    {warehouseMap[t.from_warehouse_id]?.name || "?"}
                    <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />
                    {warehouseMap[t.to_warehouse_id]?.name || "?"}
                  </span>
                </td>
                <td className="tnum px-4 py-2.5 text-right text-zinc-700">{t.items.reduce((s, i) => s + i.qty, 0)} units</td>
                <td className="px-4 py-2.5 text-zinc-500">{t.created_by_name || "-"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Delivery Note</DialogTitle>
            <DialogDescription className="sr-only">Delivery note creation form</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Warehouse</Label>
                <select data-testid="transfer-from" required value={fromId} onChange={(e) => setFromId(e.target.value)} className={selectCls}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>To Warehouse</Label>
                <select data-testid="transfer-to" required value={toId} onChange={(e) => setToId(e.target.value)} className={selectCls}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Transfer Items</Label>
                <button type="button" data-testid="transfer-add-item" onClick={() => setItems([...items, { product_id: "", qty: "" }])} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                  + Add Item
                </button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`transfer-item-row-${i}`}>
                  <div className="flex-1">
                    <SearchableSelect
                      testid={`transfer-item-product-${i}`}
                      options={stockRows.map((r) => ({ value: r.product_id, label: `${productMap[r.product_id]?.name} — stock: ${r.qty}` }))}
                      value={it.product_id}
                      onChange={(v) => setItem(i, { product_id: v })}
                      placeholder="Select product..."
                    />
                  </div>
                  <Input data-testid={`transfer-item-qty-${i}`} type="number" min="1" max={availMap[it.product_id] || undefined} required value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} placeholder="Qty" className="w-28 rounded-sm border-zinc-300" />
                  {items.length > 1 && (
                    <button type="button" data-testid={`transfer-item-remove-${i}`} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              {fromId && stockRows.length === 0 && (
                <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="transfer-no-stock-warning">
                  The source warehouse has no stock yet. Record a stock-in first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Input data-testid="transfer-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="rounded-sm border-zinc-300" />
            </div>

            <DialogFooter>
              <Button data-testid="transfer-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                <Truck className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Create Draft"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
